import { Button, Radio, Textarea } from '@mantine/core';
import { IconMessageReport } from '@tabler/icons-react';
import { useMutation } from '../useMutation';
import { useEffect, useState, type FormEvent } from 'react';
import { failureKindOf, fileIssueReport } from '../api';
import { SUPPORT_EMAIL } from '../../shared/contact';
import type { MemberReportKind } from '../../shared/contracts';
import {
  clearEvents,
  collectDiagnostics,
  type Diagnostics,
} from '../diagnostics';
import { toastFailed, toastSaved } from '../toast';
import { PortalModal } from './PortalModal';

const SEND_FAILED = `Something went wrong sending the report. Please email ${SUPPORT_EMAIL}.`;

export const DESCRIPTION_MAX = 4000;

export function reportFailedMessage(error: Error): string {
  const kind = failureKindOf(error);
  if (kind === 'rate-limited') {
    return 'That is a lot of reports. Give it a minute and try again.';
  }
  if (kind === 'network') {
    return 'We could not reach the portal. Your report is not sent.';
  }
  return SEND_FAILED;
}

const TOPICS: Record<
  MemberReportKind,
  { label: string; field: string; description: string; placeholder: string }
> = {
  bug: {
    label: 'Something’s broken',
    field: 'What went wrong?',
    description: 'What you were doing, and what you expected instead.',
    placeholder: 'My team page shows last season’s schedule.',
  },
  dropin: {
    label: 'A drop-in',
    field: 'What happened?',
    description:
      'Which drop-in, and what went wrong with the booking, payment, or refund.',
    placeholder: 'I paid for Thursday drop-in but it still shows as unbooked.',
  },
  roster: {
    label: 'My teams or clinics',
    field: 'What needs fixing?',
    description: 'Which team or clinic, and what should change.',
    placeholder: 'Please add me to the team I played on in Spring 2026.',
  },
};

const COLLAPSE_AFTER_MS = 3000;

function isWide(): boolean {
  return window.matchMedia('(min-width: 40rem)').matches;
}

function ReportTrigger({ onOpen }: { onOpen: () => void }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    const timer = setTimeout(() => setExpanded(false), COLLAPSE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [expanded]);

  function handleClick() {
    if (!expanded && !isWide()) {
      setExpanded(true);
      return;
    }
    setExpanded(false);
    onOpen();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="portal-press border-brand/40 bg-cream text-body shadow-float hover:bg-surface focus-visible:outline-brand-dark sm:shadow-card fixed right-4 bottom-4 z-40 flex h-12 min-w-12 cursor-pointer items-center rounded-lg border px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 sm:h-auto sm:px-4 sm:py-2.5 print:hidden"
    >
      <IconMessageReport
        aria-hidden
        className="text-brand-dark size-[22px] shrink-0 sm:size-[18px]"
      />
      <span
        className={`grid transition-[grid-template-columns,opacity] duration-200 ease-out motion-reduce:transition-opacity sm:grid-cols-[1fr] sm:opacity-100 ${expanded ? 'grid-cols-[1fr] opacity-100' : 'grid-cols-[0fr] opacity-0'}`}
      >
        <span className="overflow-hidden">
          <span className="block pl-2 whitespace-nowrap">Report an issue</span>
        </span>
      </span>
    </button>
  );
}

export function ReportIssueButton() {
  const [opened, setOpened] = useState(false);
  const [kind, setKind] = useState<MemberReportKind | null>(null);
  const [description, setDescription] = useState('');
  const [context, setContext] = useState<Diagnostics | null>(null);
  const send = useMutation({
    mutationFn: (report: Parameters<typeof fileIssueReport>[0]) =>
      fileIssueReport(report),
    onSuccess: () => {
      toastSaved('Thanks — we got your report.');
      setKind(null);
      setDescription('');
      clearEvents();
      setOpened(false);
    },
    onError: (error) => toastFailed(reportFailedMessage(error)),
  });
  const sending = send.isPending;

  function open() {
    setContext(collectDiagnostics());
    setOpened(true);
  }

  function close() {
    if (sending) return;
    setOpened(false);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!kind || !context || description.trim() === '') return;
    send.mutate({ kind, description, context });
  }

  return (
    <>
      <ReportTrigger onOpen={open} />

      <PortalModal opened={opened} onClose={close} title="Report an issue">
        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <Radio.Group
            label="What’s this about?"
            required
            value={kind ?? ''}
            onChange={(value) => setKind(value as MemberReportKind)}
          >
            <div className="mt-3 flex flex-col gap-3">
              {Object.entries(TOPICS).map(([value, topic]) => (
                <Radio key={value} value={value} label={topic.label} />
              ))}
            </div>
          </Radio.Group>

          <Textarea
            label={TOPICS[kind ?? 'bug'].field}
            description={TOPICS[kind ?? 'bug'].description}
            placeholder={TOPICS[kind ?? 'bug'].placeholder}
            autosize
            minRows={4}
            maxRows={10}
            maxLength={DESCRIPTION_MAX}
            required
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />

          {context ? (
            <p className="text-muted text-sm">Sent from {context.pageName}</p>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <Button
              variant="light"
              color="black"
              disabled={sending}
              onClick={close}
            >
              Not now
            </Button>
            <Button
              type="submit"
              color="brand"
              loading={sending}
              disabled={!kind || description.trim() === ''}
            >
              Send report
            </Button>
          </div>
        </form>
      </PortalModal>
    </>
  );
}
